import React, { useCallback, useEffect, useRef, useState } from 'react'

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { Box, Typography, colors } from '@material-ui/core'

import { ConfirmationDialog } from 'components'

import { KanbanColumn } from './components'

import useKanban from 'hooks/useKanban'
import useColumnStates from 'hooks/useColumnStates'

import constants from 'constants/index'

import useStyles from './styles'

const KanbanBoard = () => {
  const { KANBAN_COLUMN_ROLE_FINALIZED, DRAG_TYPE_COLUMN } =
    constants.activities

  const classes = useStyles()

  const {
    canDragColumn,
    canFinalize,
    columns,
    handleOpenCreate,
    handleOpenEdit,
  } = useKanban()

  const {
    columnStates,
    handleDeleteKanbanCard,
    handleDragEnd,
    handleLoadMoreAll,
    pendingMove,
    confirmPendingMove,
    cancelPendingMove,
  } = useColumnStates()

  const [isDragging, setIsDragging] = useState(false)

  const handleDragEndWrapped = useCallback(
    (result) => {
      setIsDragging(false)
      return handleDragEnd(result)
    },
    [handleDragEnd],
  )

  const boardWrapperRef = useRef(null)

  useEffect(() => {
    const wrapper = boardWrapperRef.current
    if (!wrapper) return undefined

    const getScrollParent = (el) => {
      let node = el.parentElement
      while (node) {
        const { overflowY } = window.getComputedStyle(node)
        if (overflowY === 'auto' || overflowY === 'scroll') return node
        node = node.parentElement
      }
      return null
    }

    const measure = () => {
      const { top } = wrapper.getBoundingClientRect()
      let available = Math.max(window.innerHeight - top - 16, 240)
      wrapper.style.maxHeight = `${available}px`

      const scrollParent = getScrollParent(wrapper)
      if (scrollParent) {
        const excess = scrollParent.scrollHeight - scrollParent.clientHeight
        if (excess > 0) {
          available = Math.max(available - excess, 240)
          wrapper.style.maxHeight = `${available}px`
        }
      }
    }

    measure()
    window.addEventListener('resize', measure)

    const resizeObserver = new ResizeObserver(() =>
      window.requestAnimationFrame(measure),
    )
    resizeObserver.observe(wrapper)

    return () => {
      window.removeEventListener('resize', measure)
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <Box className={classes.boardWrapper} ref={boardWrapperRef}>
      <DragDropContext
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEndWrapped}
      >
        <Droppable
          droppableId="board"
          direction="horizontal"
          type={DRAG_TYPE_COLUMN}
        >
          {(boardProvided) => (
            <Box
              className={classes.board}
              ref={boardProvided.innerRef}
              {...boardProvided.droppableProps}
            >
              {columns.map((column, index) => {
                const state = columnStates[String(column.id)]
                const hasMore =
                  (state?.tasks.length ?? 0) < (state?.totalCount ?? 0)
                return (
                  <Draggable
                    key={column.id}
                    draggableId={`column-${column.id}`}
                    index={index}
                    isDragDisabled={
                      !canDragColumn ||
                      column.role === KANBAN_COLUMN_ROLE_FINALIZED
                    }
                  >
                    {(provided) => (
                      <Box ref={provided.innerRef} {...provided.draggableProps}>
                        <KanbanColumn
                          column={column}
                          cards={state?.tasks ?? []}
                          totalCount={state?.totalCount ?? 0}
                          loading={state?.loading ?? true}
                          hasMore={hasMore}
                          dragHandleProps={provided.dragHandleProps}
                          isDropDisabled={
                            column.role === KANBAN_COLUMN_ROLE_FINALIZED &&
                            !canFinalize
                          }
                          isDragging={isDragging}
                          onCreateCard={handleOpenCreate}
                          onEditCard={handleOpenEdit}
                          onDeleteCard={handleDeleteKanbanCard}
                          onLoadMore={handleLoadMoreAll}
                        />
                      </Box>
                    )}
                  </Draggable>
                )
              })}
              {boardProvided.placeholder}
              <Box className={classes.boardSpacer} />
            </Box>
          )}
        </Droppable>
      </DragDropContext>
      <ConfirmationDialog
        open={Boolean(pendingMove)}
        setOpen={(value) => {
          if (!value) cancelPendingMove()
        }}
        actionAcceptButton={confirmPendingMove}
        textButton="Prosseguir"
        textButtonCancel="Voltar"
        accentColor={colors.pink[900]}
        text={
          <Typography variant="body1">
            Essa é uma atividade <strong>automática</strong> do sistema, ao
            movê-la o fluxo original será cancelado, tem certeza que deseja
            mover essa atividade?
          </Typography>
        }
      />
    </Box>
  )
}

export default KanbanBoard
